import { useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';
import { useAuthStore } from '../store/authStore';
import { updateMyProfile } from '../services/usersApi';

/**
 * Favorites helper. Reads from the auth store's cached user record and toggles
 * favorites by PATCHing /users/me with the new array, updating the store
 * optimistically and reverting on error so the heart icon stays honest.
 *
 * Two correctness details that earlier bit this:
 *  - `toggle` reads the *live* store state, never the render-time closure, so
 *    two quick taps before a re-render can't both build off the same stale
 *    array and silently drop one change.
 *  - Network writes are serialized through `favoriteWriteChain` and each sends
 *    the latest array, so concurrent toggles can't race to last-writer-wins on
 *    the server. We don't overwrite the store with a write's response (it may
 *    be stale by the time it resolves) — the optimistic state is authoritative
 *    and a fresh bootstrap reconciles on next sign-in.
 */
let favoriteWriteChain: Promise<unknown> = Promise.resolve();

export function useFavorites() {
  const { appUser } = useAuth();
  // Memoize so the array reference is stable across renders (the `?? []`
  // fallback would otherwise be a fresh array each render, busting the
  // useCallback below and any consumer that depends on `favoriteIds`).
  const favoriteIds = useMemo(() => appUser?.favoriteItemIds ?? [], [appUser?.favoriteItemIds]);

  const isFavorite = useCallback(
    (itemId: string): boolean => favoriteIds.includes(itemId),
    [favoriteIds],
  );

  const toggle = useCallback(async (itemId: string): Promise<void> => {
    const current = useAuthStore.getState().appUser;
    if (current === null) return;
    const wasFavorite = current.favoriteItemIds.includes(itemId);
    const optimistic = wasFavorite
      ? current.favoriteItemIds.filter((id) => id !== itemId)
      : [...current.favoriteItemIds, itemId];
    // Optimistic update off the freshest state.
    useAuthStore.getState().setAppUser({ ...current, favoriteItemIds: optimistic });

    favoriteWriteChain = favoriteWriteChain
      .catch(() => undefined) // a prior write's failure must not block this one
      .then(async () => {
        const live = useAuthStore.getState().appUser;
        if (live === null) return;
        try {
          await updateMyProfile({ favoriteItemIds: live.favoriteItemIds });
        } catch {
          // Roll just this item back against the latest state, so a concurrent
          // toggle of a *different* item isn't clobbered by the rollback.
          const latest = useAuthStore.getState().appUser;
          if (latest === null) return;
          const reverted = wasFavorite
            ? [...latest.favoriteItemIds.filter((id) => id !== itemId), itemId]
            : latest.favoriteItemIds.filter((id) => id !== itemId);
          useAuthStore.getState().setAppUser({ ...latest, favoriteItemIds: reverted });
        }
      });
    await favoriteWriteChain;
  }, []);

  return { favoriteIds, isFavorite, toggle };
}
